import React, { useEffect, useRef, useState } from 'react';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import {
  Box,
  Card,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { Device } from '../Log/graph.util';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GaugeChart from 'react-gauge-chart';

interface DetailedBatteryViewSodistoreProps {
  // SodistoreGrid uses composite "{cluster}-{device}" IDs (e.g. "1-1"),
  // so this is intentionally widened — the value is only rendered, never Number()'d.
  batteryId: number | string;
  s3Credentials: I_S3Credentials;
  batteryData: Device;
  installationId: number;
  productNum: number;
}

function DetailedBatteryViewSodistore(
  props: DetailedBatteryViewSodistoreProps
) {
  if (props.batteryData === null) {
    return null;
  }

  const navigate = useNavigate();

  const handleBatteryViewButton = () => {
    navigate(location.pathname.split('/').slice(0, -2).join('/'));
  };

  const batteryStyle = {
    borderRadius: '15px',
    padding: '10px',
    backgroundColor: 'lightgray',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '150px'
  };

  const batteryStringStyle = {
    flex: 1,
    border: '1px solid #000',
    height: '97%',
    width: '30px',
    borderRadius: '7px',
    backgroundColor: '#bfbfbf'
  };

  const cellVoltagesArray: number[] =
    props.batteryData.BatteryDeligreenDataRecord.CellVoltage.split(',')
      .map((v) => parseFloat(v.trim()))
      .filter((v) => !isNaN(v)); // Filter invalid numbers

  const minVoltage = Math.min(...cellVoltagesArray);
  const maxVoltage = Math.max(...cellVoltagesArray);

  const SocGauge = ({ soc }: { soc: number }) => {
    const [stableSoc, setStableSoc] = useState(soc);
    const isFirstRender = useRef(true);

    useEffect(() => {
      // On first render, don't animate — just set
      if (isFirstRender.current) {
        setStableSoc(soc);
        isFirstRender.current = false;
      } else {
        // Only update if the change is significant enough to justify it
        if (Math.abs(soc - stableSoc) > 0.1) {
          setStableSoc(soc);
        }
      }
    }, [soc]);

    return (
      <div style={{ height: '40px', width: '85%', textAlign: 'center' }}>
        <GaugeChart
          id="soc-gauge"
          nrOfLevels={30}
          colors={['#FF0000', '#FFD700', '#00FF00']}
          arcWidth={0.3}
          percent={stableSoc / 100}
          textColor="transparent"
          animate={false} // Won’t work, but no harm
          needleColor="#464A4F"
        />
        <div style={{ marginTop: '0px', fontWeight: 'bold', fontSize: '18px' }}>
          <div>SOC: {stableSoc.toFixed(1)}%</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Grid container>
        <Grid item xs={6} md={6}>
          <IconButton
            aria-label="go back"
            sx={{
              marginTop: '30px',
              backgroundColor: 'grey',
              color: '#000000',
              '&:hover': { bgcolor: '#f7b34d' }
            }}
            onClick={handleBatteryViewButton}
          >
            <ArrowBackIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Grid container justifyContent="center">
        <Grid
          item
          xs={6}
          md={6}
          sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem' }}
        >
          Node {props.batteryId}
        </Grid>
      </Grid>
      <Grid item md={3} xs={3}>
        <Card
          sx={{
            overflow: 'visible',
            marginTop: '30px',
            marginBottom: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '2px solid #ccc',
            borderRadius: '12px'
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              marginTop: '10px',
              borderBottom: '1px solid #ccc',
              fontWeight: 'bold'
            }}
          >
            Battery Information
          </Typography>

          <TableContainer
            component={Paper}
            sx={{ marginTop: '20px', width: '100%' }}
          >
            <Table size="medium" aria-label="a dense table">
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Bus Voltage
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord.BusVoltage +
                      ' V'}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Battery Voltage
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord
                      .TotalBatteryVoltage + ' V'}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Charge/Discharge Current
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord.BusCurrent +
                      ' A'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Residual Capacity
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord
                      .ResidualCapacity + ' Ah'}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor:
                        props.batteryData.BatteryDeligreenDataRecord
                          .BatteryCapacity <
                        props.batteryData.BatteryDeligreenDataRecord
                          .RatedCapacity
                          ? '#FF033E'
                          : ''
                    }}
                  >
                    Battery Capacity
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px',
                      backgroundColor:
                        props.batteryData.BatteryDeligreenDataRecord
                          .BatteryCapacity <
                        props.batteryData.BatteryDeligreenDataRecord
                          .RatedCapacity
                          ? '#FF033E'
                          : ''
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord
                      .BatteryCapacity + ' Ah'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    SoC
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord.Soc + '  %'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Rated Capacity
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord
                      .RatedCapacity + ' Ah'}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    Number of Cycles
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {
                      props.batteryData.BatteryDeligreenDataRecord
                        .NumberOfCycles
                    }
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    SOH
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {props.batteryData.BatteryDeligreenDataRecord.Soh + ' %'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
      {/*----------------------------------------------------------------------------------------------------------------------------------*/}
      <Grid item md={3} xs={12}>
        {/* SOC Card */}
        <Card
          sx={{
            overflow: 'visible',
            marginTop: '30px',
            marginLeft: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '2px solid #ccc',
            borderRadius: '12px',
            padding: '10px',
            height: '240px'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              borderBottom: '1px solid #ccc',
              marginBottom: '10px'
            }}
          >
            SOC
          </Typography>
          <SocGauge soc={props.batteryData.BatteryDeligreenDataRecord.Soc} />
        </Card>

        {/* On-Off State Card */}
        {/*----------------------------------------------------------------------------------------------------------------------------------*/}

        <Card
          sx={{
            overflow: 'visible',
            marginTop: '20px',
            marginLeft: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '2px solid #ccc',
            borderRadius: '12px'
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              marginTop: '10px',
              borderBottom: '1px solid #ccc',
              fontWeight: 'bold'
            }}
          >
            On-Off State
          </Typography>

          <TableContainer
            component={Paper}
            sx={{ marginTop: '20px', width: '100%' }}
          >
            <Table size="medium" aria-label="a dense table">
              <TableBody>
                {[
                  [
                    'Charge Switch State',
                    props.batteryData.BatteryDeligreenAlarmRecord.OnOffState
                      .ChargeSwitchState
                  ],
                  [
                    'Discharge Switch State',
                    props.batteryData.BatteryDeligreenAlarmRecord.OnOffState
                      .DischargeSwitchState
                  ],
                  [
                    'Current Limit Switch State',
                    props.batteryData.BatteryDeligreenAlarmRecord.OnOffState
                      .CurrentLimitSwitchStat
                  ],
                  [
                    'Heating Switch State',
                    props.batteryData.BatteryDeligreenAlarmRecord.OnOffState
                      .HeatingSwitchState
                  ]
                ].map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                      {label}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: '6ch',
                        whiteSpace: 'nowrap',
                        paddingRight: '12px'
                      }}
                    >
                      {value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>
      {/*----------------------------------------------------------------------------------------------------------------------------------*/}

      <Grid item md={3} xs={12}>
        <Grid
          container
          spacing={2}
          sx={{
            marginTop: '30px',
            marginLeft: '20px',
            marginBottom: '30px',
            border: '2px solid #ccc',
            borderRadius: '12px',
            padding: '20px',
            width: '95%',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Paper
                elevation={4}
                sx={{
                  width: '105px',
                  height: '80px',
                  backgroundColor: '#32CD32',
                  borderRadius: '10px 0 0 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  marginBottom: '15px'
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Current
                </Typography>
                <Typography variant="h6">
                  {props.batteryData.BatteryDeligreenDataRecord.BusCurrent.toFixed(
                    3
                  )}{' '}
                  A
                </Typography>
              </Paper>
              <Box
                sx={{
                  width: '12px',
                  height: '40px',
                  backgroundColor: '#666',
                  borderRadius: '0 4px 4px 0',
                  marginLeft: '2px'
                }}
              />
            </Box>
          </Grid>

          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Paper
                elevation={4}
                sx={{
                  width: '105px',
                  height: '80px',
                  backgroundColor:
                    props.batteryData.BatteryDeligreenDataRecord
                      .TotalBatteryVoltage < 32 ||
                    props.batteryData.BatteryDeligreenDataRecord
                      .TotalBatteryVoltage > 63
                      ? '#FF033E'
                      : '#32CD32',
                  borderRadius: '10px 0 0 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  marginBottom: '15px'
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Voltage
                </Typography>
                <Typography variant="h6">
                  {props.batteryData.BatteryDeligreenDataRecord.TotalBatteryVoltage.toFixed(
                    3
                  )}{' '}
                  V
                </Typography>
              </Paper>
              <Box
                sx={{
                  width: '12px',
                  height: '40px',
                  backgroundColor: '#666',
                  borderRadius: '0 4px 4px 0',
                  marginLeft: '2px'
                }}
              />
            </Box>
          </Grid>
        </Grid>
        {/*----------------------------------------------------------------------------------------------------------------------------------*/}

        <Card
          sx={{
            overflow: 'visible',
            marginTop: '-5px',
            marginLeft: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: '2px solid #ccc',
            borderRadius: '12px'
          }}
        >
          <TableContainer
            component={Paper}
            sx={{ marginTop: '0px', width: '100%' }}
          >
            <Table size="medium" aria-label="a dense table">
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    align="left"
                    sx={{ fontWeight: 'bold' }}
                  >
                    System State
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: '6ch',
                      whiteSpace: 'nowrap',
                      paddingRight: '12px'
                    }}
                  >
                    {Object.entries(
                      props.batteryData.BatteryDeligreenAlarmRecord.SystemState
                    ).find(([_, value]) => value === 'True')?.[0] ?? 'Unknown'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {/*----------------------------------------------------------------------------------------------------------------------------------*/}
        {/*Temperature List*/}

        {/*<Card*/}
        {/*  sx={{*/}
        {/*    overflow: 'visible',*/}
        {/*    marginTop: '20px',*/}
        {/*    marginLeft: '20px',*/}
        {/*    display: 'flex',*/}
        {/*    flexDirection: 'column',*/}
        {/*    alignItems: 'center',*/}
        {/*    border: '2px solid #ccc',*/}
        {/*    borderRadius: '12px',*/}
        {/*    height: '270px'*/}
        {/*  }}*/}
        {/*>*/}
        {/*  <Typography*/}
        {/*    variant="h6"*/}
        {/*    component="div"*/}
        {/*    sx={{*/}
        {/*      marginTop: '10px',*/}
        {/*      borderBottom: '1px solid #ccc',*/}
        {/*      fontWeight: 'bold'*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    Battery Temperatures*/}
        {/*  </Typography>*/}

        {/*  <TableContainer*/}
        {/*    component={Paper}*/}
        {/*    sx={{*/}
        {/*      marginTop: '20px',*/}
        {/*      width: '100%',*/}
        {/*      maxHeight: '270px',*/}
        {/*      overflowY: 'auto'*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    <Table size="medium" stickyHeader aria-label="temperature table">*/}
        {/*      <TableBody>*/}
        {/*        {Object.entries(*/}
        {/*          props.batteryData.BatteryDeligreenDataRecord*/}
        {/*            .TemperaturesList || {}*/}
        {/*        ).map(([label, value]) => (*/}
        {/*          <TableRow key={label}>*/}
        {/*            <TableCell align="left" sx={{ fontWeight: 'bold' }}>*/}
        {/*              {label}*/}
        {/*            </TableCell>*/}
        {/*            <TableCell*/}
        {/*              align="right"*/}
        {/*              sx={{*/}
        {/*                width: '6ch',*/}
        {/*                whiteSpace: 'nowrap',*/}
        {/*                paddingRight: '12px'*/}
        {/*              }}*/}
        {/*            >*/}
        {/*              {value + ' °C'}*/}
        {/*            </TableCell>*/}
        {/*          </TableRow>*/}
        {/*        ))}*/}
        {/*      </TableBody>*/}
        {/*    </Table>*/}
        {/*  </TableContainer>*/}
        {/*</Card>*/}
      </Grid>

      {/*----------------------------------------------------------------------------------------------------------------------------------*/}

      <Grid item md={3} xs={3}>
        <Grid
          container
          spacing={1}
          sx={{
            marginTop: '30px',
            marginLeft: '20px',
            border: '2px solid #ccc',
            borderRadius: '12px',
            padding: '10px',
            width: '370px'
          }}
        >
          {[0, 1].map((col) => (
            <Grid item xs={6} key={col}>
              <Grid container direction="column" spacing={1}>
                {cellVoltagesArray
                  .slice(col * 8, (col + 1) * 8)
                  .map((voltage, index) => (
                    <Grid item key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Paper
                          elevation={3}
                          sx={{
                            backgroundColor:
                              voltage < 1.82 || voltage > 3.94
                                ? '#FF033E'
                                : voltage < 2 || voltage > 3.8
                                ? '#ffbf00'
                                : '#32CD32',
                            padding: '8px',
                            minWidth: '145px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderRadius: '8px 0 0 8px'
                          }}
                        >
                          <Typography variant="body2">
                            Cell {col * 8 + index + 1 < 10 ? '0' : ''}
                            {col * 8 + index + 1}
                          </Typography>
                          <Typography variant="body2">
                            {voltage.toFixed(3)} V
                          </Typography>
                        </Paper>
                        {/* Battery Cap */}
                        <Box
                          sx={{
                            width: '10px',
                            height: '40px',
                            backgroundColor: '#666',
                            borderRadius: '0 4px 4px 0',
                            marginLeft: '2px'
                          }}
                        />
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/*Alarm events 1-3 */}
      {/*----------------------------------------------------------------------------------------------------------------------------------*/}
      <Grid container spacing={2}>
        {[1, 2, 3].map((eventNum) => {
          const activeAlarms = Object.entries(
            props.batteryData.BatteryDeligreenAlarmRecord[
              `AlarmEvent${eventNum}`
            ] || {}
          ).filter(([_, value]) => value === 'True');

          return (
            <Grid item xs={12} md={4} key={eventNum}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  border: '2px solid #ccc',
                  borderRadius: '12px',
                  height: `${Math.min(80 + activeAlarms.length * 30, 300)}px`
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '10px',
                    marginBottom: '5px',
                    borderBottom: '1px solid #ccc',
                    fontWeight: 'bold'
                  }}
                >
                  Alarm Event {eventNum}
                </Typography>

                <Box
                  sx={{
                    overflowY: activeAlarms.length > 0 ? 'auto' : 'visible',
                    width: '100%',
                    px: 1,
                    flex: 1
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        {activeAlarms.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No active alarms
                            </TableCell>
                          </TableRow>
                        ) : (
                          activeAlarms.map(([label, value]) => (
                            <TableRow key={label}>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {label}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ paddingRight: '12px' }}
                              >
                                {value}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/*Alarm events 4-6 */}
      {/*----------------------------------------------------------------------------------------------------------------------------------*/}
      <Grid container spacing={2}>
        {[4, 5, 6].map((eventNum) => {
          const activeAlarms = Object.entries(
            props.batteryData.BatteryDeligreenAlarmRecord[
              `AlarmEvent${eventNum}`
            ] || {}
          ).filter(([_, value]) => value === 'True');

          return (
            <Grid item xs={12} md={4} key={eventNum}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  border: '2px solid #ccc',
                  borderRadius: '12px',
                  height: `${Math.min(80 + activeAlarms.length * 30, 300)}px`,
                  marginTop: '20px'
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '10px',
                    marginBottom: '5px',
                    borderBottom: '1px solid #ccc',
                    fontWeight: 'bold'
                  }}
                >
                  Alarm Event {eventNum}
                </Typography>

                <Box
                  sx={{
                    overflowY: activeAlarms.length > 0 ? 'auto' : 'visible',
                    width: '100%',
                    px: 1,
                    flex: 1
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        {activeAlarms.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No active alarms
                            </TableCell>
                          </TableRow>
                        ) : (
                          activeAlarms.map(([label, value]) => (
                            <TableRow key={label}>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {label}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ paddingRight: '12px' }}
                              >
                                {value}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/*Alarm events 7,8, CellsAlarms */}
      {/*----------------------------------------------------------------------------------------------------------------------------------*/}
      <Grid container spacing={2}>
        {[7, 8].map((eventNum) => {
          const activeAlarms = Object.entries(
            props.batteryData.BatteryDeligreenAlarmRecord[
              `AlarmEvent${eventNum}`
            ] || {}
          ).filter(([_, value]) => value === 'True');

          return (
            <Grid item xs={12} md={4} key={`alarm-${eventNum}`}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  border: '2px solid #ccc',
                  borderRadius: '12px',
                  marginTop: '20px',
                  height: `${Math.min(80 + activeAlarms.length * 30, 300)}px`
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '10px',
                    marginBottom: '5px',
                    borderBottom: '1px solid #ccc',
                    fontWeight: 'bold'
                  }}
                >
                  Alarm Event {eventNum}
                </Typography>

                <Box
                  sx={{
                    overflowY: activeAlarms.length > 0 ? 'auto' : 'visible',
                    width: '100%',
                    px: 1,
                    flex: 1
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        {activeAlarms.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No active alarms
                            </TableCell>
                          </TableRow>
                        ) : (
                          activeAlarms.map(([label, value]) => (
                            <TableRow key={label}>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {label}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ paddingRight: '12px' }}
                              >
                                {value}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Card>
            </Grid>
          );
        })}

        {/* Third Column: CellAlarmList */}
        <Grid item xs={12} md={4}>
          {(() => {
            const cellAlarms = (
              props.batteryData.BatteryDeligreenAlarmRecord.CellAlarmList || ''
            )
              .split(',')
              .map((item) => item.trim())
              .map((value, index) => ({
                label: `Cell ${index + 1 < 10 ? '0' : ''}${index + 1}`,
                value
              }))
              .filter(
                ({ value }) =>
                  value !== 'Normal, no alarm' && value !== 'Normal-no alarm'
              ); // handles both formats

            return (
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  border: '2px solid #ccc',
                  borderRadius: '12px',
                  marginTop: '20px',
                  height: `${Math.min(80 + cellAlarms.length * 30, 300)}px`
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '10px',
                    marginBottom: '5px',
                    borderBottom: '1px solid #ccc',
                    fontWeight: 'bold'
                  }}
                >
                  Cell Alarms
                </Typography>

                <Box
                  sx={{
                    overflowY: cellAlarms.length > 0 ? 'auto' : 'visible',
                    width: '100%',
                    px: 1,
                    flex: 1
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        {cellAlarms.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No active alarms
                            </TableCell>
                          </TableRow>
                        ) : (
                          cellAlarms.map(({ label, value }) => (
                            <TableRow key={label}>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {label}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ paddingRight: '12px' }}
                              >
                                {value}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Card>
            );
          })()}
        </Grid>
      </Grid>

      {/*CellTemperatureAlarms */}
      {/*----------------------------------------------------------------------------------------------------------------------------------*/}
      <Grid container spacing={2}>
        {/* Third Column: CellAlarmList */}
        <Grid item xs={12} md={4}>
          {(() => {
            const cellTemperatureAlarms = (
              props.batteryData.BatteryDeligreenAlarmRecord
                .CellTemperatureAlarm || ''
            )
              .split(',')
              .map((item) => item.trim())
              .map((value, index) => ({
                label: `Cell ${index + 1 < 10 ? '0' : ''}${index + 1}`,
                value
              }))
              .filter(
                ({ value }) =>
                  value !== 'Normal, no alarm' && value !== 'Normal-no alarm'
              ); // handles both formats

            return (
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  border: '2px solid #ccc',
                  borderRadius: '12px',
                  marginTop: '20px',
                  marginBottom: '20px',
                  height: `${Math.min(
                    80 + cellTemperatureAlarms.length * 30,
                    300
                  )}px`
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '10px',

                    borderBottom: '1px solid #ccc',
                    fontWeight: 'bold'
                  }}
                >
                  Cell Alarms
                </Typography>

                <Box
                  sx={{
                    overflowY:
                      cellTemperatureAlarms.length > 0 ? 'auto' : 'visible',
                    width: '100%',
                    px: 1,
                    flex: 1
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        {cellTemperatureAlarms.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No active alarms
                            </TableCell>
                          </TableRow>
                        ) : (
                          cellTemperatureAlarms.map(({ label, value }) => (
                            <TableRow key={label}>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {label}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ paddingRight: '12px' }}
                              >
                                {value}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Card>
            );
          })()}
        </Grid>
      </Grid>
    </>
  );
}

export default DetailedBatteryViewSodistore;
